import {startOfToday} from 'date-fns';
import {format as formatDate} from 'date-fns/format';
import {
  Button,
  closeCurrentDialog,
  openSuccessDialog,
  showNotification,
  useDialog,
} from 'platform/components';
import {Hide, HStack, Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback, useMemo} from 'react';

// eslint-disable-next-line no-restricted-imports -- Intended
import {head, path} from 'ramda';
import {isString} from 'ramda-adjunct';

import {
  SourcingVehicleListItemResponseBody,
  useAuctionBulkCreateUserComparisonMutation,
  useAuctionBulkCreateUserFavouriteMutation,
  useAuctionBulkCreateUserHiddenMutation,
  useAuctionBulkCreateUserToBuyMutation,
  useAuctionBulkDeleteUserFavouriteMutation,
  useAuctionBulkDeleteUserHiddenMutation,
  useAuctionBulkDeleteUserToBuyMutation,
  useSourcingBulkCreateUserCarvagoBuyMutation,
  useSourcingBulkCreateUserComparisonMutation,
  useSourcingBulkCreateUserFavouriteMutation,
  useSourcingBulkCreateUserHiddenMutation,
  useSourcingBulkCreateUserToBuyMutation,
  useSourcingBulkDeleteUserFavouriteMutation,
  useSourcingBulkDeleteUserHiddenMutation,
  useSourcingBulkDeleteUserToBuyMutation,
  useSourcingExportVehiclesToCsvMutation,
  useSourcingPushVehiclesToSaleMutation,
} from '@dms/api/core';
import featureFlags from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {getSafely, isNilOrZero, openInNewTab} from 'shared/web';

import {CARVAGO_TRANSACTION_WORKSPACE_URLS} from '../../../constants/carvagoTransactionWorkspaceUrls';
import {useCurrentWorkspace} from '../../../hooks/useCurrentWorkspace';
import {useSourcingCurrency} from '../../../hooks/useSourcingCurrency';
import {useSourcingPage} from '../../../hooks/useSourcingPage';
import {useSourcingTargetCountry} from '../../../hooks/useSourcingTargetCountry';
import {SourcingType, useSourcingType} from '../../../hooks/useSourcingType';
import {SourcingPage} from '../../../types/SourcingPage';
import {downloadCsv} from '../../../utils/downloadCsv';
import {SourcingCarvagoBuyDialog} from '../../SourcingCarvagoBuyDialog/SourcingCarvagoBuyDialog';

export type DataViewBulkActionDrawerProps = {
  pathToVehicleAdId: string[] | null;
  selectedRows: SourcingVehicleListItemResponseBody[];
  onUnselectSelectedRows: (rowIds: string[]) => void;
};

const getButtonTranslations = (page: SourcingPage) =>
  match(page)
    .with(SourcingPage.DEFAULT, () => ({
      buy: i18n.t('general.actions.buy'),
      comparison: i18n.t('general.actions.addToComparison'),
      export: null as unknown,
      favourites: i18n.t('general.actions.addToFavourites'),
      hide: i18n.t('general.actions.hide'),
      buyNow: null as unknown,
      carvagoBuy: i18n.t('general.actions.addToCarvagoBuy'),
    }))
    .with(SourcingPage.CART_PAGE, () => ({
      buy: i18n.t('general.actions.removeFromBasket'),
      comparison: i18n.t('general.actions.comparison'),
      export: i18n.t('general.actions.export'),
      favourites: i18n.t('general.actions.favourites'),
      hide: i18n.t('general.actions.hide'),
      buyNow: i18n.t('general.actions.buyNow'),
      carvagoBuy: i18n.t('general.actions.addToCarvagoBuy'),
    }))
    .with(SourcingPage.HIDDEN_PAGE, () => ({
      buy: null as unknown,
      comparison: null as unknown,
      export: null as unknown,
      favourites: null as unknown,
      hide: i18n.t('general.actions.removeFromHidden'),
      buyNow: null as unknown,
      carvagoBuy: null as unknown,
    }))
    .with(SourcingPage.FAVORITES_PAGE, () => ({
      buy: i18n.t('general.actions.buy'),
      comparison: i18n.t('general.actions.comparison'),
      export: null as unknown,
      favourites: i18n.t('general.actions.removeFromFavourites'),
      hide: i18n.t('general.actions.hide'),
      buyNow: null as unknown,
      carvagoBuy: i18n.t('general.actions.addToCarvagoBuy'),
    }))
    .with(SourcingPage.CARVAGO_BUY_PAGE, () => ({
      buy: i18n.t('general.actions.buy'),
      comparison: i18n.t('general.actions.comparison'),
      export: null as unknown,
      favourites: i18n.t('general.actions.favourites'),
      hide: i18n.t('general.actions.hide'),
      buyNow: null as unknown,
      carvagoBuy: null as unknown,
    }))
    .otherwise(() => ({
      buy: null as unknown,
      comparison: null as unknown,
      export: null as unknown,
      favourites: null as unknown,
      hide: null as unknown,
      buyNow: null as unknown,
      carvagoBuy: null as unknown,
    }));

export function DataViewBulkActionDrawer(props: DataViewBulkActionDrawerProps) {
  const currency = useSourcingCurrency();
  const currentWorkspace = useCurrentWorkspace();
  const targetCountry = useSourcingTargetCountry();

  const [createSourcingFavourite] = useSourcingBulkCreateUserFavouriteMutation();
  const [deleteSourcingFavourite] = useSourcingBulkDeleteUserFavouriteMutation();
  const [createAuctionFavourite] = useAuctionBulkCreateUserFavouriteMutation();
  const [deleteAuctionFavourite] = useAuctionBulkDeleteUserFavouriteMutation();
  const [createSourcingComparison] = useSourcingBulkCreateUserComparisonMutation();
  const [createAuctionComparison] = useAuctionBulkCreateUserComparisonMutation();
  const [createSourcingHidden] = useSourcingBulkCreateUserHiddenMutation();
  const [deleteSourcingHidden] = useSourcingBulkDeleteUserHiddenMutation();
  const [createAuctionHidden] = useAuctionBulkCreateUserHiddenMutation();
  const [deleteAuctionHidden] = useAuctionBulkDeleteUserHiddenMutation();
  const [createSourcingToBuy] = useSourcingBulkCreateUserToBuyMutation();
  const [deleteSourcingToBuy] = useSourcingBulkDeleteUserToBuyMutation();
  const [createAuctionToBuy] = useAuctionBulkCreateUserToBuyMutation();
  const [deleteAuctionToBuy] = useAuctionBulkDeleteUserToBuyMutation();
  const [createSourcingCarvagoBuy] = useSourcingBulkCreateUserCarvagoBuyMutation();
  const [pushVehiclesToSale] = useSourcingPushVehiclesToSaleMutation();
  const [exportVehicleList] = useSourcingExportVehiclesToCsvMutation();
  const [isCarvagoBuyConfirmOpen, openCarvagoBuyConfirm, closeCarvagoBuyConfirm] = useDialog();

  const page = useSourcingPage();
  const type = useSourcingType();
  const selectedVehiclesCount = props.selectedRows.length;
  const selectorKey = head(props.pathToVehicleAdId ?? []) as string;

  const isSold = useMemo(
    () =>
      props.selectedRows.some((row) => {
        const availability =
          path([selectorKey, 'isAvailable'], row) ?? path([selectorKey, 'atAuction'], row);
        return availability == null ? true : !availability;
      }),
    [props.selectedRows, selectorKey]
  );

  const getIds = useCallback(
    (
      type: 'favourite' | 'comparison' | 'inCart' | 'hidden' | 'carvagoBuy',
      isChecked = false
    ): string[] =>
      props.selectedRows
        .filter((vehicle) => getSafely(vehicle, type) !== !isChecked)
        .map((vehicle) => path(props.pathToVehicleAdId as string[], vehicle)) as string[],
    [props.selectedRows, props.pathToVehicleAdId]
  );

  const cartButtonIds = getIds('inCart', page === SourcingPage.CART_PAGE);
  const onClickVehicleCartButton = () => {
    if (page === SourcingPage.CART_PAGE) {
      const mutation = type === SourcingType.AUCTIONS ? deleteAuctionToBuy : deleteSourcingToBuy;
      mutation({body: {externalIds: cartButtonIds}})
        .unwrap()
        .then(() => props.onUnselectSelectedRows(cartButtonIds))
        .catch(handleApiError);
    } else {
      const mutation = type === SourcingType.AUCTIONS ? createAuctionToBuy : createSourcingToBuy;
      mutation({body: {externalIds: cartButtonIds}})
        .unwrap()
        .then(() =>
          showNotification.success(
            `${i18n.t('entity.vehicle.notifications.vehiclesAddedToCard')} (${
              cartButtonIds.length
            })`
          )
        )
        .catch(handleApiError);
    }
  };

  const handleExport = () =>
    exportVehicleList({
      externalIds: getIds('inCart', true),
      currency: currency?.code,
      buyerCountry: targetCountry?.code,
    })
      .unwrap()
      .then((csvContent) => {
        if (isString(csvContent)) {
          downloadCsv(
            csvContent,
            `${currentWorkspace?.name}_${formatDate(startOfToday(), 'yyyyMMdd')}.csv`
          );
        } else {
          throw new Error('CSV content must be string.');
        }
      })
      .catch(handleApiError);

  const handleBuy = () => {
    pushVehiclesToSale({
      externalIds: getIds('inCart', true),
    })
      .unwrap()
      .then(() =>
        showNotification.success(i18n.t('entity.vehicle.notifications.vehiclesPushedToSales'))
      )
      .catch(handleApiError);
    props.onUnselectSelectedRows(cartButtonIds);
  };

  const carvagoBuyButtonIds = getIds('carvagoBuy', false);
  const onClickCarvagoBuyButton = () => {
    openCarvagoBuyConfirm();
  };
  const handleConfirmCarvagoBuy = (_message: string) => {
    createSourcingCarvagoBuy({
      bulkCreateUserCarvagoBuyRequestBody: {externalIds: carvagoBuyButtonIds},
    })
      .unwrap()
      .then(() =>
        openSuccessDialog({
          id: 'sourcing-carvago-buy-bulk-success-dialog',
          heading: i18n.t('entity.sourcing.notifications.carvagoBuySentTitle'),
          subheading: i18n.t('entity.sourcing.notifications.carvagoBuySentToCarvago', {
            count: carvagoBuyButtonIds.length,
          }),
          secondaryButtonLabel: i18n.t('general.actions.trackRequest'),
          onSecondaryButtonClick: () => {
            const url =
              process.env.ENV_TYPE === 'prod'
                ? CARVAGO_TRANSACTION_WORKSPACE_URLS.prod
                : CARVAGO_TRANSACTION_WORKSPACE_URLS.other;
            openInNewTab(url);
          },
          buttonLabel: i18n.t('general.actions.close'),
          onClose: closeCurrentDialog,
          'data-testid': testIds.sourcing.classifieds('bulkActionDrawer-carvagoBuySuccessDialog'),
        })
      )
      .catch(handleApiError);
    closeCarvagoBuyConfirm();
  };

  const CartButton = (
    <Button
      title={`${getButtonTranslations(page).buy} (${cartButtonIds.length})`}
      leftIcon="action/shopping_cart"
      variant={page === SourcingPage.FAVORITES_PAGE ? 'primary' : 'outlined'}
      isDisabled={isNilOrZero(cartButtonIds.length)}
      onClick={onClickVehicleCartButton}
      data-testid={testIds.sourcing.classifieds('bulkActionDrawer-cartButton')}
    />
  );

  const favouriteButtonIds = getIds('favourite', page === SourcingPage.FAVORITES_PAGE);
  const createFavourite = (externalIds: string[]) =>
    type === SourcingType.AUCTIONS
      ? createAuctionFavourite({body: {externalIds}})
      : createSourcingFavourite({body: {externalIds}});
  const deleteFavourite = (externalIds: string[]) =>
    type === SourcingType.AUCTIONS
      ? deleteAuctionFavourite({body: {externalIds}})
      : deleteSourcingFavourite({body: {externalIds}});
  const onClickFavouriteButton = () => {
    if (page === SourcingPage.FAVORITES_PAGE) {
      deleteFavourite(favouriteButtonIds)
        .unwrap()
        .then(() => props.onUnselectSelectedRows(favouriteButtonIds))
        .catch(handleApiError);
    } else {
      createFavourite(favouriteButtonIds)
        .unwrap()
        .then(() =>
          showNotification.success(
            `${i18n.t('entity.vehicle.notifications.vehiclesAddedToFavourite')} (${
              favouriteButtonIds.length
            })`
          )
        )
        .catch(handleApiError);
    }
  };

  const FavouriteButton = (
    <Button
      title={`${getButtonTranslations(page).favourites} (${favouriteButtonIds.length})`}
      leftIcon="toggle/star"
      variant={page === SourcingPage.DEFAULT ? 'primary' : 'outlined'}
      isDisabled={isNilOrZero(favouriteButtonIds.length)}
      onClick={onClickFavouriteButton}
      data-testid={testIds.sourcing.classifieds('bulkActionDrawer-favouriteButton')}
    />
  );

  if (isSold && page === SourcingPage.CART_PAGE) {
    return (
      <HStack spacing={2} justify="flex-end">
        {CartButton}
      </HStack>
    );
  }

  if (isSold && page === SourcingPage.FAVORITES_PAGE) {
    return (
      <HStack spacing={2} justify="flex-end">
        {FavouriteButton}
      </HStack>
    );
  }

  const hiddenButtonIds = getIds('hidden', page === SourcingPage.HIDDEN_PAGE);
  const comparisonButtonIds = getIds('comparison', false);
  const onClickHideButton = () => {
    if (page === SourcingPage.HIDDEN_PAGE) {
      const mutation = type === SourcingType.AUCTIONS ? deleteAuctionHidden : deleteSourcingHidden;
      mutation({body: {externalIds: hiddenButtonIds}})
        .unwrap()
        .then(() => props.onUnselectSelectedRows(hiddenButtonIds))
        .catch(handleApiError);
    } else {
      const mutation = type === SourcingType.AUCTIONS ? createAuctionHidden : createSourcingHidden;
      mutation({body: {externalIds: hiddenButtonIds}})
        .unwrap()
        .then(() => props.onUnselectSelectedRows(hiddenButtonIds))
        .then(() =>
          showNotification.success(
            `${i18n.t('entity.vehicle.notifications.vehiclesAddedToHidden')} (${
              hiddenButtonIds.length
            })`
          )
        )
        .catch(handleApiError);
    }
  };
  const onClickComparisonButton = () => {
    const mutation =
      type === SourcingType.AUCTIONS ? createAuctionComparison : createSourcingComparison;
    mutation({body: {externalIds: comparisonButtonIds}})
      .unwrap()
      .then(() =>
        showNotification.success(
          `${i18n.t('entity.vehicle.notifications.vehiclesAddedToComparison')} (${
            comparisonButtonIds.length
          })`
        )
      )
      .catch(handleApiError);
  };

  return (
    <HStack spacing={2} justify="flex-end">
      <Hide when={page === SourcingPage.CART_PAGE}>
        <Button
          title={`${getButtonTranslations(page).hide} (${hiddenButtonIds.length})`}
          leftIcon="action/visibility_off"
          isDisabled={isNilOrZero(hiddenButtonIds.length)}
          variant="outlined"
          onClick={onClickHideButton}
          data-testid={testIds.sourcing.classifieds('bulkActionDrawer-hideButton')}
        />
      </Hide>
      <Hide when={page === SourcingPage.HIDDEN_PAGE}>
        <Button
          title={`${getButtonTranslations(page).comparison} (${comparisonButtonIds.length})`}
          variant="outlined"
          leftIcon="action/compare_arrows"
          isDisabled={isNilOrZero(comparisonButtonIds.length)}
          onClick={onClickComparisonButton}
          data-testid={testIds.sourcing.classifieds('bulkActionDrawer-comparisonButton')}
        />
        {CartButton}
        {FavouriteButton}
        <Show
          when={type !== SourcingType.AUCTIONS && page !== SourcingPage.CARVAGO_BUY_PAGE}
          whenFeatureEnabled={featureFlags.SOURCING_CARVAGO_BUY}
        >
          <Button
            title={`${getButtonTranslations(page).carvagoBuy} (${carvagoBuyButtonIds.length})`}
            leftIcon="custom/carvago"
            variant="outlined"
            isDisabled={isNilOrZero(carvagoBuyButtonIds.length)}
            onClick={onClickCarvagoBuyButton}
            data-testid={testIds.sourcing.classifieds('bulkActionDrawer-carvagoBuyButton')}
          />
        </Show>
        <Show when={page === SourcingPage.CART_PAGE}>
          <Button
            title={`${getButtonTranslations(page).export} (${selectedVehiclesCount})`}
            variant="outlined"
            leftIcon="content/save_alt"
            onClick={handleExport}
            data-testid={testIds.sourcing.classifieds('bulkActionDrawer-exportButton')}
          />
          <Button
            title={`${getButtonTranslations(page).buyNow} (${selectedVehiclesCount})`}
            variant="primary"
            leftIcon="maps/local_mall"
            onClick={handleBuy}
            data-testid={testIds.sourcing.classifieds('bulkActionDrawer-buyNowButton')}
          />
        </Show>
      </Hide>
      <SourcingCarvagoBuyDialog
        isOpen={isCarvagoBuyConfirmOpen}
        onClose={closeCarvagoBuyConfirm}
        onConfirm={handleConfirmCarvagoBuy}
        data-testid={testIds.sourcing.classifieds('bulkActionDrawer-carvagoBuyDialog')}
      />
    </HStack>
  );
}
